import React, { useMemo, useState } from 'react';
import { Box, Tabs, Tab, Typography, makeStyles } from '@material-ui/core';
import { RangeValue } from '../constants/product.constant';
import { authService } from '../services';
import RangeSelector from '../components/analytics/RangeSelector';
import OverviewTab from './dashboard/OverviewTab';
import RecentSignupsTab from './dashboard/RecentSignupsTab';
import SuperPrepTab from './dashboard/SuperPrepTab';
import SuperCoachTab from './dashboard/SuperCoachTab';
import TeacherAppTab from './dashboard/TeacherAppTab';
import TestAccountsTab from './dashboard/TestAccountsTab';

const useStyles = makeStyles(() => ({
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 8,
  },
  header: { fontWeight: 600 },
  tabs: { borderBottom: '1px solid #e5e7eb', marginBottom: 20 },
}));

// `windowed` tabs respond to the shared range selector; others are self-contained.
// `product` (if set) gates the tab by the admin's product access.
type TabDef = { label: string; windowed: boolean; product?: string; render: (days: RangeValue) => React.ReactNode };
const ALL_TABS: TabDef[] = [
  { label: 'Overview', windowed: true, render: (d) => <OverviewTab days={d} /> },
  { label: 'Recent Signups', windowed: false, render: () => <RecentSignupsTab /> },
  { label: 'Super Prep', windowed: true, product: 'SUPER_PREP', render: (d) => <SuperPrepTab days={d} /> },
  { label: 'Super Coach', windowed: true, product: 'SUPER_COACH', render: (d) => <SuperCoachTab days={d} /> },
  { label: 'Teacher App', windowed: true, product: 'TEACHER_APP', render: (d) => <TeacherAppTab days={d} /> },
  { label: 'Test Accounts', windowed: false, render: () => <TestAccountsTab /> },
];

const DashboardScreen: React.FC = () => {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const [days, setDays] = useState<RangeValue>('30d');

  // Only show tabs whose product the admin can access (non-product tabs always show).
  const TABS = useMemo(() => ALL_TABS.filter((t) => !t.product || authService.hasProduct(t.product)), []);
  const current = TABS[tab] || TABS[0];

  return (
    <div>
      <div className={classes.headerRow}>
        <Typography variant="h5" className={classes.header}>Analytics</Typography>
        {current.windowed && <RangeSelector value={days} onChange={setDays} />}
      </div>

      <Tabs
        className={classes.tabs}
        value={tab < TABS.length ? tab : 0}
        onChange={(_, v) => setTab(v)}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
      >
        {TABS.map((t) => <Tab key={t.label} label={t.label} />)}
      </Tabs>

      <Box>{current.render(days)}</Box>
    </div>
  );
};

export default DashboardScreen;
